/**
 * Calculates the number of decimal places in a given number.
 * @param {number} num - The number to calculate decimal places for.
 * @returns {number} - The number of decimal places in the given number.
 */
export const countDecimal = num => {
    return num !== Math.floor(num)
        ? num.toString().split('.')[1].length || 0
        : 0;
};

/**
 * Rounds a number to a specified number of decimal places.
 * @param {number} num
 * @param {number} precision - max decimal places
 * @returns {number} The rounded number.
 */
export const roundToNum = (num, precision) => {
    return +(Math.round(num + `e+${precision}`) + `e-${precision}`);
};

/**
 * An array of byte size units in increasing order.
 * 0: 𝘣𝘺𝘵𝘦𝘴
 * 1: 𝙠𝙞𝙡𝙤𝘣𝘺𝘵𝘦𝘴
 * 2: 𝙢𝙚𝙜𝙖𝘣𝘺𝘵𝘦𝘴
 * 3: 𝗴𝗶𝗴𝗮𝘣𝘺𝘵𝘦𝘴
 * 4: 𝙩𝙚𝙧𝙖𝘣𝘺𝘵𝘦𝘴
 * @type {ReadonlyArray<string>}
 */
export const byteSizeUnits = Object.freeze(['B', 'kB', 'MB', 'GB', 'TB']);

/**
 * Convert file size in bytes to a human-readable file size
 * @param {string | number} size
 * @return {string | number}
 * @author FM
 */
export const humanFileSize = size => {
    let n = size;
    if (n && parseInt(n) >= 1) {
        if (!Number.isInteger(n)) {
            n = Number(n);
        }
        const M = Math;
        const i = M.floor(M.log(n) / M.log(1024));
        return `${(n / 1024 ** i).toFixed(2) * 1} ${byteSizeUnits[i]}`;
    }
    return 0;
};

/**
 * Return true if the value is a finite number
 * @param value
 * @return {boolean}
 * @author FM
 */
export const isNumber = value => {
    if (
        'number' !== typeof value ||
        //FM: Consider this as the NaN check.
        value !== Number(value) ||
        false === Number.isFinite(value)
    ) {
        return false;
    }
    return true;
};
// Q: it might not be enough native check Number.isFinite ?

/**
 * Check if value is a number excluding null, false, undefined and including 0 and other number in string format
 * @param {mixed} value - The value to evaluate
 * @returns {boolean} - Return true if value is consider a number
 * @private
 * @author mm
 */
export const isNumeric = value => !isNaN(parseInt(value));

/**
 * Checks if a given value is a positive integer.
 * @param {any} value - The value to check.
 * @returns {boolean}
 */
export const isPositiveInteger = value =>
    value ? Number.isInteger(value) && 0 < Math.sign(value) : false;

/**
 * Clamp a number within the inclusive range specified by boundary values.
 * @param {number} num - The number to clamp.
 * @param {Object} [boundary] - The boundary values to clamp the number within.
 * @param {number} [boundary.min=num] - The minimum boundary value.
 * @param {number} [boundary.max=num] - The maximum boundary value.
 * @returns {number} The clamped number.
 */
export const clamp = (num, boundary) => {
    let result = num;
    if (boundary) {
        const { min = num, max = num } = boundary;
        if (min > max || max < min) {
            console.error(boundary);
        } else if (num < min) {
            result = min;
        } else if (num > max) {
            result = max;
        }
    }
    return result;
};

/**
 * Generates a random integer thath is no lower than min and is less than (but not equal to) max.
 * @param {number} min - The minimum value of the range (inclusive).
 * @param {number} max - The maximum value of the range (exclusive).
 * @returns {number} A random integer between min (inclusive) and max (exclusive).
 */
export const getRandomInt = (min, max) => {
    const M = Math;
    const i = M.ceil(min);
    return M.floor(M.random() * (M.floor(max) - i)) + i;
};

/**
 * Calculates the percentage of a given total value
 * @param {number} percentage - The percentage to calculate.
 * @param {number} total - The total value to calculate the percentage from.
 * @param {number} [precision=2] - Optional: The number of decimal places to round the result to. Defaults to 2.
 * @returns {number} The calculated percentage value.
 */
export const calcPercentOf = (percentage, total, precision = 2) => {
    return +((percentage / 100) * total).toFixed(precision);
};

/**
 * Rounds up a number to the nearest multiple of a given step.
 * @param {number} num - The number to be rounded up.
 * @param {number} step - The multiple to round up to.
 * @returns {number} The rounded up number.
 */
export const ceilMod = (num, step) => {
    const n = parseInt(num / step, 10) * step;
    return n + step;
};

/**
 * Calculates the penultimate block range based on a given block size and total count.
 * @param {number} blockSize module.
 * @param {number} total max limit.
 * @returns {?[number, number]} [start,end] indices of the penultimate block, or null if there is no penultimate block.
 */
export const getPenultimateRange = (blockSize, total) => {
    let range = null;
    const M = Math;
    const n = M.ceil(total / blockSize); // blocks
    if (n > 1) {
        const lastBlockStart = blockSize * (n - 1);
        const start = M.max(0, lastBlockStart - blockSize);
        const end = M.min(total, start + blockSize);
        range = [start, end];
    }
    return range;
};

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// Q: should be moved into a dedicated html Helper ?
/**
 * Regular expression to match HTML entity codes in the format &#(decimalNumber);
 * @type {RegExp}
 */
const htmlEntityRegex = /&#(\d+);/g;

/**
 * Replaces numeric HTML entities with their corresponding characters.
 * @param {str} string - The string to decode.
 * @returns {string} The decoded string.
 */
export const decodeNumericHtmlEntities = str =>
    str.replace(htmlEntityRegex, (match, dec) => String.fromCharCode(dec));
